import {
  mixed,
  number,
  object,
  boolean,
  string,
  array,
  AnySchema,
  ObjectSchema,
} from "yup";
import { isNil, omit } from "lodash";

import {
  CoverageAnimalLiability,
  CoverageBoolean,
  CoverageDamageToPropertyOfOthers,
  CoverageDebrisRemoval,
  CoverageDeductibles,
  CoverageELiability,
  CoverageEquipmentBreakdown,
  CoverageFMedicalPaymentToOthers,
  CoverageFungiWetOrDryRotOrBacteriaProperty,
  CoverageIdentityFraudExpense,
  CoverageLossAssessment,
  CoverageMineSubsidenceScheduled,
  CoverageMineSubsidenceScheduledItem,
  CoveragePackageKeys,
  CoverageResidenceBurglary,
  CoverageScheduledPersonalPropertyItem,
  CoverageSecondaryResidenceLiability,
  CoverageSecondaryResidenceLiabilityAddress,
  CoverageWaterBackupAndSumpOverflow,
  CoverageWithSettlementOption,
  Operator,
  OptionType,
  PolicyLevelCoverages,
  SettlementOption,
  UnitLevelCoverages,
  coveragePackageKeys,
  namesOfDeductibles,
} from "@covertree/new-business-graphql";
import { coverageEffectiveDate } from "@covertree/utils";
import {
  CoverageAccessor,
  CoverageValueFormats,
  ExtraCoverageAccessor,
  extraCoverageAccessors,
  getExtraCoveragesMap,
  getPackageCoveragesMap,
  GetOptionalWithOptions,
  GetRequiredWithOptions,
  mapDollarsEnumToNumber,
  mapNumberEnumToNumber,
  mapPercentEnumToDisplayValue,
  mapSettlementOptionToDisplayValue,
  PackageCoverageAccessor,
  ScreenWithCoverages,
} from "@covertree/new-business-mappings";
import { UnitWithCoverages } from "@covertree/new-business-redux";
import {
  secondaryResidenceLiabilityValidation,
  mineSubsidenceScheduledValidation,
  scheduledPersonalPropertyValidation,
} from "./extraCoveragesForm";

export type CustomizationValue =
  | string
  | number
  | boolean
  | CoverageSecondaryResidenceLiabilityAddress[]
  | undefined
  | null;

export interface CustomizePlanCoverage {
  validationKey: ValidationKey;
  accessor: CoverageAccessor;
  details: {
    image: string;
    getName: GetRequiredWithOptions;
    getDescription: GetOptionalWithOptions;
    getTooltip: GetOptionalWithOptions;
    valueFormats?: CoverageValueFormats;
  };
  /**
   * Field type comes from typename of validation object or coverage itself
   */
  fieldType: string;
  disabled: boolean;
  options?: (OptionType | string | number)[];
  step?: number | null;
  min?: number;
  max?: number;
  isExtraCoverage: boolean;
  defaultValue?: CustomizationValue;
}

export type ValueKey =
  | "value"
  | "settlementOption"
  | (typeof namesOfDeductibles)[number];

export type CoverageKey =
  | Exclude<keyof UnitLevelCoverages, "__typename">
  | Exclude<keyof PolicyLevelCoverages, "__typename">;

export type ValidationKey = `${CoverageKey}_${ValueKey}`;

export type PolicyCustomizationForm = {
  effectiveDate?: string;
  policyLevelCoverages: Partial<
    Record<
      ValidationKey,
      | CoverageScheduledPersonalPropertyItem[]
      | CustomizationValue
      | null
      | undefined
    >
  >;
  units: {
    exposureLocator: string;
    unitLevelCoverages: Record<ValidationKey, CustomizationValue>;
  }[];
};

const canBeOptionalValidation = (
  schema: AnySchema,
  validationKey: ValidationKey,
  canBeOptional = false
) => {
  const accessor = mapValidationKeyToAccessor(validationKey);

  if (extraCoverageAccessors.includes(accessor) || canBeOptional)
    return schema.optional().nullable();
  return schema.required();
};

const unitLevelCoveragesValidation = (
  unitLevelCoverages: UnitLevelCoverages
) => {
  const packageCoverageMap = getPackageCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const extraCoverageMap = getExtraCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const schema = Object.entries(omit(unitLevelCoverages, "__typename"))
    .map(([key, value]) => ({ key, ...value }))
    .reduce(
      (schema, coverage) => {
        const { key: id, __typename } = coverage;
        const key = id as CoverageKey;

        if (__typename === "CoverageSecondaryResidenceLiability") {
          const validationKey = `${key}_value` as ValidationKey;
          schema[validationKey] =
            secondaryResidenceLiabilityValidation.nullable();
          return schema;
        }

        if (__typename === "CoverageMineSubsidenceScheduled") {
          const validationKey = `${key}_value` as ValidationKey;

          schema[validationKey] = array()
            .of(mineSubsidenceScheduledValidation)
            .nullable();

          return schema;
        }

        if (__typename === "CoverageDeductibles") {
          namesOfDeductibles.forEach((deductibleName) => {
            const validationKey = `${key}_${deductibleName}` as ValidationKey;

            const displayName = (() => {
              const accessor = mapValidationKeyToAccessor(validationKey);
              const description =
                packageCoverageMap[accessor as PackageCoverageAccessor];

              return description?.getName() || accessor;
            })();

            const valueCustomization =
              coverage[`${deductibleName}Customization`];

            if (coverage[`${deductibleName}IsHidden`] || !valueCustomization) {
              schema[validationKey] = number().nullable();
              return schema;
            }

            if (valueCustomization?.__typename === "DropdownCustomization") {
              if (valueCustomization.options.length > 0) {
                schema[validationKey] = number().oneOf(
                  valueCustomization.options.map((option) => +option),
                  `${displayName} has incorrect value`
                );
              } else {
                schema[validationKey] = number().nullable();
              }
            }

            if (
              valueCustomization?.__typename ===
              "SliderCustomizationWithNumberOptions"
            ) {
              schema[validationKey] = number().oneOf(
                valueCustomization?.options || [],
                `${displayName} has incorrect value`
              );
            }

            if (valueCustomization?.constraint) {
              const dependsOnDisplayName = (() => {
                const description =
                  packageCoverageMap[
                    valueCustomization.constraint
                      .dependsOn as PackageCoverageAccessor
                  ];
                return (
                  description?.getName() ||
                  valueCustomization.constraint?.dependsOn
                );
              })();

              switch (valueCustomization.constraint.operator) {
                case Operator.LessThanOrEqual:
                  schema[validationKey] = schema[validationKey].concat(
                    number().when(
                      mapAccessorToValidationKey(
                        valueCustomization.constraint.dependsOn
                      ),
                      ([dependsOnValue], schema) =>
                        schema.max(
                          dependsOnValue,
                          `${displayName} cannot be higher than ${dependsOnDisplayName}`
                        )
                    )
                  );

                  break;
                case Operator.MoreThanOrEqual:
                  schema[validationKey] = schema[validationKey].concat(
                    number().when(
                      mapAccessorToValidationKey(
                        valueCustomization.constraint.dependsOn
                      ),
                      ([dependsOnValue], schema) =>
                        schema.min(
                          dependsOnValue,
                          `${displayName} cannot be lower than ${dependsOnDisplayName}`
                        )
                    )
                  );
                  break;
                case Operator.Equal:
                  schema[validationKey] = schema[validationKey].concat(
                    number().when(
                      mapAccessorToValidationKey(
                        valueCustomization.constraint.dependsOn
                      ),
                      ([dependsOnValue], schema) =>
                        schema.test(
                          "value-equal-to-constraint",
                          `${displayName} should be equal to ${dependsOnDisplayName}`,
                          (value) => value === dependsOnValue
                        )
                    )
                  );
              }
            }
          });

          return schema;
        }

        if (__typename === "CoverageWithSettlementOption") {
          const validationKey = `${key}_settlementOption` as ValidationKey;

          const { settlementOptionCustomization, settlementOptionIsHidden } =
            coverage;

          const displayName = (() => {
            const accessor = mapValidationKeyToAccessor(validationKey);
            const description =
              packageCoverageMap[accessor as PackageCoverageAccessor];
            return description?.getName() || accessor;
          })();

          if (settlementOptionIsHidden) {
            schema[validationKey] = mixed().nullable();
          } else {
            schema[validationKey] = mixed().oneOf(
              settlementOptionCustomization?.options || [],
              `${displayName} has incorrect value`
            );
          }
          // don't return schema here, cause we wanna value validation to be added
        }

        const validationKey = `${key}_value` as ValidationKey;
        const displayName = (() => {
          const accessor = mapValidationKeyToAccessor(validationKey);

          const description = extraCoverageAccessors.includes(accessor)
            ? extraCoverageMap[accessor as ExtraCoverageAccessor]
            : packageCoverageMap[accessor as PackageCoverageAccessor];

          return description?.getName() || accessor;
        })();

        if ("valueIsHidden" in coverage && coverage.valueIsHidden) {
          schema[validationKey] = mixed().nullable();
          return schema;
        }

        if (__typename === "CoverageBoolean") {
          schema[validationKey] = boolean().typeError(
            `${displayName} should have falsy value`
          );
          schema[validationKey] = canBeOptionalValidation(
            schema[validationKey],
            validationKey
          );
          return schema;
        }

        const valueCustomization = (
          coverage as Exclude<
            UnitLevelCoverages[keyof UnitLevelCoverages],
            | CoverageDeductibles
            | CoverageBoolean
            | CoverageEquipmentBreakdown
            | CoverageSecondaryResidenceLiability
            | CoverageMineSubsidenceScheduled
            | string
            | undefined
          >
        ).valueCustomization;

        if (!valueCustomization) {
          schema[validationKey] = canBeOptionalValidation(
            mixed(),
            validationKey
          );
          return schema;
        }

        if (valueCustomization.__typename === "DropdownCustomization") {
          schema[validationKey] = string().oneOf(
            valueCustomization.options.map((option) => option.toString()),
            `${displayName} has incorrect value`
          );
          schema[validationKey] = canBeOptionalValidation(
            schema[validationKey],
            validationKey
          );
        }

        if (
          valueCustomization.__typename ===
          "DropdownCustomizationWithNumberOptions"
        ) {
          schema[validationKey] = number().oneOf(
            valueCustomization.options.map((option) => option),
            `${displayName} has incorrect value`
          );
          schema[validationKey] = canBeOptionalValidation(
            schema[validationKey],
            validationKey
          );
        }

        if (
          valueCustomization.__typename ===
          "DropdownCustomizationWithExtendedOptions"
        ) {
          schema[validationKey] = number().oneOf(
            valueCustomization.options.map(
              (option: OptionType) => option.value
            ),
            `${displayName} has incorrect value`
          );
          schema[validationKey] = canBeOptionalValidation(
            schema[validationKey],
            validationKey
          );
        }

        if (
          [
            "SliderCustomization",
            "SliderCustomizationWithExtendedOptions",
            "SliderCustomizationWithNumberOptions",
          ].includes(valueCustomization.__typename as string)
        ) {
          schema[validationKey] = number().typeError(
            `${displayName} is required`
          );
          schema[validationKey] = canBeOptionalValidation(
            schema[validationKey],
            validationKey,
            "min" in valueCustomization && valueCustomization.min === 0
          );

          if (
            valueCustomization.__typename === "SliderCustomization" &&
            !isNil(valueCustomization.min) &&
            !isNil(valueCustomization.max)
          ) {
            let minLimit = valueCustomization.min;

            if (
              key === "coverageA" &&
              (coverage as CoverageWithSettlementOption).settlementOption ===
                SettlementOption.ExtendedReplacementCost
            ) {
              const coverageValue = (coverage as CoverageWithSettlementOption)
                .value;

              if (coverageValue && coverageValue < valueCustomization.min) {
                minLimit = coverageValue;
              }
            }

            schema[validationKey] = schema[validationKey].concat(
              number()
                .min(
                  minLimit,
                  `${displayName} cannot be lower than ${valueCustomization.min}`
                )
                .max(
                  valueCustomization.max,
                  `${displayName} cannot be higher than ${valueCustomization.max}`
                )
            );
          }

          if (
            valueCustomization.__typename ===
            "SliderCustomizationWithNumberOptions"
          ) {
            if (valueCustomization.options?.length > 0) {
              schema[validationKey] = schema[validationKey].concat(
                number().oneOf(
                  valueCustomization.options,
                  `${displayName} has incorrect value`
                )
              );
            }
          }

          if (
            valueCustomization.__typename ===
            "SliderCustomizationWithExtendedOptions"
          ) {
            if (valueCustomization.options?.length > 0) {
              schema[validationKey] = schema[validationKey].concat(
                number().oneOf(
                  valueCustomization.options.map((opt) => opt.value),
                  `${displayName} has incorrect value`
                )
              );
            }
          }
        }

        if (valueCustomization.constraint) {
          const dependsOnDisplayName = (() => {
            const description =
              packageCoverageMap[
                valueCustomization.constraint
                  .dependsOn as PackageCoverageAccessor
              ] ||
              extraCoverageMap[
                valueCustomization.constraint.dependsOn as ExtraCoverageAccessor
              ];

            return (
              description?.getName() || valueCustomization.constraint?.dependsOn
            );
          })();

          switch (valueCustomization.constraint.operator) {
            case Operator.LessThanOrEqual:
              schema[validationKey] = schema[validationKey].concat(
                number().when(
                  mapAccessorToValidationKey(
                    valueCustomization.constraint.dependsOn
                  ),
                  ([dependsOnValue], schema) =>
                    canBeOptionalValidation(
                      schema.max(
                        dependsOnValue,
                        `${displayName} cannot be higher than ${dependsOnDisplayName}`
                      ),
                      validationKey
                    )
                )
              );
              break;
            case Operator.MoreThanOrEqual:
              schema[validationKey] = schema[validationKey].concat(
                number().when(
                  mapAccessorToValidationKey(
                    valueCustomization.constraint.dependsOn
                  ),
                  ([dependsOnValue], schema) =>
                    canBeOptionalValidation(
                      schema.min(
                        dependsOnValue,
                        `${displayName} cannot be lower than ${dependsOnDisplayName}`
                      ),
                      validationKey
                    )
                )
              );
              break;
            case Operator.Equal:
              schema[validationKey] = schema[validationKey].concat(
                number().when(
                  mapAccessorToValidationKey(
                    valueCustomization.constraint.dependsOn
                  ),
                  ([dependsOnValue], schema) =>
                    canBeOptionalValidation(
                      schema.test(
                        "value-equal-to-constraint",
                        `${displayName} should be equal to ${dependsOnDisplayName}`,
                        (value) => value === dependsOnValue
                      ),
                      validationKey
                    )
                )
              );
          }
        }

        return schema;
      },
      {} as Record<ValidationKey, AnySchema>
    );
  return object().shape(schema);
};

export const policyCustomizationValidations = (
  policyLevelCoverages?: PolicyLevelCoverages,
  unitLevelCoverages?: UnitLevelCoverages,
  isBookConversionPolicy = false
) => {
  if (!policyLevelCoverages || !unitLevelCoverages)
    throw new Error(
      "Policy level coverages and unit level coverages are required"
    );

  const schema = object({
    effectiveDate: coverageEffectiveDate(
      "Coverage effective date",
      isBookConversionPolicy
    ),
    policyLevelCoverages: object({
      scheduledPersonalProperty_value: array()
        .of(scheduledPersonalPropertyValidation)
        .nullable(),
      identityFraudExpense_value: string().optional().nullable(),
    }),
    units: array().of(
      object({
        exposureLocator: string().optional().nullable(),
        unitLevelCoverages: unitLevelCoveragesValidation(unitLevelCoverages),
      })
    ),
  });
  return schema as ObjectSchema<PolicyCustomizationForm>;
};

export const getDefaultValuesForCustomization = (
  effectiveDate: string,
  policyLevelCoverages: PolicyLevelCoverages,
  units: UnitWithCoverages[],
  revisionDependentAccessors: string[],
  unitsWithCoveragesOptions: UnitWithCoverages[]
) => ({
  effectiveDate: effectiveDate,
  policyLevelCoverages: Object.entries(
    omit(policyLevelCoverages, "__typename")
  ).reduce(
    (defaultValues, [id, coverage]) => {
      const { __typename } = coverage;
      if (__typename === "CoverageScheduledPersonalProperty") {
        const validationKey = `${id}_value` as ValidationKey;

        if (coverage.valueIsHidden) return defaultValues;
        defaultValues[validationKey] = coverage.value ?? undefined;
        return defaultValues;
      }
      if (__typename === "CoverageIdentityFraudExpense") {
        const validationKey = `${id}_value` as ValidationKey;

        if (coverage.valueIsHidden) return defaultValues;
        defaultValues[validationKey] = coverage.value ?? undefined;
        return defaultValues;
      }
      return defaultValues;
    },
    {} as Record<
      ValidationKey,
      CoverageScheduledPersonalPropertyItem[] | CustomizationValue | undefined
    >
  ),
  units: units.map(({ exposureLocator, unitLevelCoverages }) => ({
    exposureLocator,
    unitLevelCoverages: Object.entries(
      omit(unitLevelCoverages, "__typename")
    ).reduce(
      (defaultValues, [id, coverage]) => {
        const { __typename } = coverage;
        if (revisionDependentAccessors.includes(id)) return defaultValues;

        if (__typename === "CoverageDeductibles") {
          namesOfDeductibles.forEach((deductibleName) => {
            const validationKey = `${id}_${deductibleName}` as ValidationKey;

            if (!coverage[deductibleName]) return defaultValues;
            defaultValues[validationKey] = mapDollarsEnumToNumber(
              coverage[deductibleName]
            );
          });
          return defaultValues;
        }

        if (__typename === "CoverageSecondaryResidenceLiability") {
          const validationKey = `${id}_value` as ValidationKey;

          if (coverage.valueIsHidden) return defaultValues;
          defaultValues[validationKey] = coverage.value ?? undefined;
          return defaultValues;
        }

        if (__typename === "CoverageWithSettlementOption") {
          const validationKey = `${id}_settlementOption` as ValidationKey;

          const defaultValue = unitsWithCoveragesOptions.find(
            (unit) => unit.exposureLocator === exposureLocator
          )!.unitLevelCoverages[
            id as keyof UnitLevelCoverages
          ] as CoverageWithSettlementOption;

          if (
            !defaultValue.settlementOption &&
            !defaultValue.defaultSettlementOption
          )
            return defaultValues;

          defaultValues[validationKey] = mapSettlementOptionToDisplayValue(
            coverage.settlementOption ||
              defaultValue.settlementOption ||
              defaultValue.defaultSettlementOption
          );
          // don't return defaultValues here, cause we wanna covA/covB/covC value to be added
        }

        if (
          (
            coverage as Exclude<
              UnitLevelCoverages[keyof UnitLevelCoverages],
              CoverageDeductibles | string
            >
          )?.valueIsHidden &&
          !(
            coveragePackageKeys.includes(
              id as unknown as CoveragePackageKeys
            ) &&
            !isNil(
              (
                coverage as Exclude<
                  UnitLevelCoverages[keyof UnitLevelCoverages],
                  CoverageDeductibles | string
                >
              )?.value
            )
          )
        )
          return defaultValues;

        const validationKey = `${id}_value` as ValidationKey;

        defaultValues[validationKey] = mapGraphQLValuesToFormValues(
          coverage as Exclude<
            UnitLevelCoverages[keyof UnitLevelCoverages],
            CoverageDeductibles | string
          >
        ) as CustomizationValue;

        return defaultValues;
      },
      {} as Record<ValidationKey, CustomizationValue>
    ),
  })),
});

export const mapGraphQLValuesToFormValues = (
  coverage?: UnitLevelCoverages[Exclude<
    keyof UnitLevelCoverages,
    "__typename" | "deductibles"
  >]
):
  | CustomizationValue
  | CoverageScheduledPersonalPropertyItem[]
  | CoverageMineSubsidenceScheduledItem[] => {
  if (coverage?.__typename === "CoverageWithSettlementOption")
    return coverage?.value || 0;

  if (typeof coverage?.value !== "string") return coverage?.value;

  if (coverage.__typename === "CoverageGolfCart")
    return mapNumberEnumToNumber(coverage.value);

  if (
    coverage.__typename === "CoverageDLossOfUse" ||
    coverage.__typename === "CoverageEarthquake" ||
    coverage.__typename === "CoverageInflationGuard" ||
    coverage.__typename === "CoverageWaterDamage"
  )
    return mapPercentEnumToDisplayValue(coverage.value);

  return mapDollarsEnumToNumber(
    (
      coverage as
        | CoverageAnimalLiability
        | CoverageDamageToPropertyOfOthers
        | CoverageDebrisRemoval
        | CoverageELiability
        | CoverageEquipmentBreakdown
        | CoverageFMedicalPaymentToOthers
        | CoverageFungiWetOrDryRotOrBacteriaProperty
        | CoverageIdentityFraudExpense
        | CoverageLossAssessment
        | CoverageWaterBackupAndSumpOverflow
        | CoverageResidenceBurglary
    )?.value
  );
};

export const mapAccessorToValidationKey = (
  accessor: CoverageAccessor | string
) => accessor.replace(".", "_") as ValidationKey;

export const mapValidationKeyToAccessor = (
  validationKey: ValidationKey | string
) => validationKey.replace("_", ".") as CoverageAccessor;
